import React, { useState, useEffect } from "react";
import { Download, ArrowLeft, Plus, Trash2, X, Save, Sparkles, Target, Activity,AlertTriangle,ClipboardCheck } from 'lucide-react';
import { useParams, useNavigate } from "react-router-dom";
import { MdDelete, MdAddTask, MdClose, MdDownload } from "react-icons/md";
import { IoMdAddCircleOutline } from "react-icons/io";
import { motion, AnimatePresence } from 'framer-motion';
import {
  createWorksheet,
  getWorksheetsByNode,
  updateWorksheet,
  deleteWorksheet,
  getAISuggestions,
} from "../../services/operations/worksheetAPI";
import toast from "react-hot-toast";

const guideWordOptions = ["No", "Less", "More", "As Well As", "Part Of", "Other Than", "Reverse"];
const parameterOptions = [
  "Containment", "Flow", "Temperature", "Pressure", "Level",
  "Composition", "Mixing", "Weight", "PHRAs", "Services/Utilities",
  "Personnel Safety", "Maintenance", "Reaction"
];
const leadingConsequencesOptions = [
  "Toxic Material Release & Injury", "Toxic Material Release & serious injury", "Toxic Material Release & fatality",
  "Fire & Explosion leading to fatality", "Fire & Explosion leading to Burn", "Fire & Explosion leading to Property Damage",
  "Environment Loss", "Business & Brand Loss", "Serious Health Impact"
];

const WorksheetPage = () => {
  const { nodeId } = useParams();
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorksheets();
  }, []);

  const fetchWorksheets = async () => {
    try {
      setLoading(true);
      const data = await getWorksheetsByNode(nodeId);
      
      const transformedWorksheets = (data.data || []).map((worksheet) => {
        const [guideWord, parameter] = worksheet.guideWordParameter.split("+"); 
        return { 
          ...worksheet, 
          guideWord, 
          parameter 
        };
      });
  
      setWorksheets(transformedWorksheets);
    } catch (error) {
      console.error("Error fetching worksheets:", error);
      toast.error("Failed to load worksheets.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAISuggestions = async (index) => {
    const worksheet = worksheets[index];
    if (!worksheet.guideWord || !worksheet.parameter) return;

    try {
      const data = await getAISuggestions(worksheet.guideWord, worksheet.parameter);

      if (data) {
        const updatedWorksheets = [...worksheets];
        updatedWorksheets[index] = {
          ...updatedWorksheets[index],
          deviation: data.deviation || "",
          causesOptions: data.causes?.map((item) => item.cause) || [],
          consequencesOptions: data.causes?.flatMap((item) => item.consequences) || [],
          existingSafeguardOptions: data.existingSafeguards || [],
          recommendationOptions: data.recommendations || [],
        };
        setWorksheets(updatedWorksheets);
      }
    } catch (error) {
      console.error("Error fetching AI suggestions:", error);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedWorksheets = [...worksheets];
    updatedWorksheets[index][field] = value;

    if (field === "guideWord" || field === "parameter") {
      updatedWorksheets[index].deviation = "";
      fetchAISuggestions(index);
    }

    if (field === "severity" || field === "likelihood") {
      const severity = Number(updatedWorksheets[index].severity) || 0;
      const likelihood = Number(updatedWorksheets[index].likelihood) || 0;
      updatedWorksheets[index].risk = severity * likelihood;
    }

    const first = updatedWorksheets[index].firstConsequence;
    const second = updatedWorksheets[index].secondConsequence;
    if (first && second) {
      const combined = `${first} leading to ${second}`;
      if (!updatedWorksheets[index].consequences.includes(combined)) {
        updatedWorksheets[index].consequences.push(combined);
      }
      updatedWorksheets[index].firstConsequence = "";
      updatedWorksheets[index].secondConsequence = "";
    }

    setWorksheets(updatedWorksheets);
  };

  const handleMultiSelect = (index, field, selectedValue) => {
    if (!selectedValue) return;

    setWorksheets((prev) => {
      const newData = [...prev];
      if (!newData[index][field].includes(selectedValue)) {
        newData[index][field].push(selectedValue);
      }
      return newData;
    });
  };

  const handleManualInput = (index, field, manualValue) => {
    if (!manualValue.trim()) return;

    setWorksheets((prev) => {
      const newData = [...prev];
      if (!newData[index][field].includes(manualValue.trim())) {
        newData[index][field].push(manualValue.trim());
      }
      return newData;
    });
  };

  const handleRemoveSelection = (index, field, valueToRemove) => {
    setWorksheets((prev) => {
      const newData = [...prev];
      if (field === "causes") {
        newData[index].causes = newData[index].causes.filter((item) => item !== valueToRemove);
      } else if (field === "consequences") {
        newData[index].consequences = newData[index].consequences.filter((item) => item !== valueToRemove);
      } else {
        newData[index][field] = newData[index][field].filter((item) => item !== valueToRemove);
      }
      return newData;
    });
  };

  const handleSave = async (index) => {
    const worksheet = worksheets[index];

    if (!worksheet.guideWord || !worksheet.parameter || !worksheet.severity || !worksheet.likelihood) {
      return toast.error("Please fill in all required fields.");
    }

    try {
      const formattedWorksheet = {
        ...worksheet,
        guideWordParameter: `${worksheet.guideWord}+${worksheet.parameter}`,
        nodeId,
      };

      if (worksheet._id) {
        await updateWorksheet(worksheet._id, formattedWorksheet);
        toast.success("Worksheet updated successfully");
      } else {
        await createWorksheet(formattedWorksheet);
        toast.success("Worksheet created successfully");
        fetchWorksheets();
      }
    } catch (error) {
      console.error("Save Error:", error);
      toast.error("Failed to save worksheet");
    }
  };

  const handleDelete = async (index, worksheetId) => {
    if (worksheetId) {    
      try {
        await deleteWorksheet(worksheetId);
        toast.success("Worksheet deleted successfully");
        fetchWorksheets();
      } catch (error) {
        console.error("Delete Error:", error);
        toast.error("Failed to delete worksheet");
      }
    } else {
      setWorksheets(worksheets.filter((_, i) => i !== index));
    }
  };

  const handleAddRow = () => {
    setWorksheets([
      ...worksheets,
      {
        guideWord: "",
        parameter: "",
        deviation: "",
        causes: [],
        consequences: [],
        severity: "",
        likelihood: "",
        risk: "",
        existingSafeguard: [],
        recommendation: [],
        causesOptions: [],
        consequencesOptions: [],
        leadingConsequencesOptions: [],
        existingSafeguardOptions: [],
        recommendationOptions: [],
        firstConsequence: "",
        secondConsequence: ""
      },
    ]);
  };

  const getRiskColor = (risk) => {
    if (risk < 1) return "bg-white/20 text-slate-600";
    if (risk < 4) return "bg-gradient-to-r from-emerald-400/80 to-green-500/80 text-white backdrop-blur-sm";
    if (risk >= 4 && risk <= 9) return "bg-gradient-to-r from-yellow-400/80 to-amber-500/80 text-slate-900 backdrop-blur-sm";
    if (risk >= 10 && risk <= 16) return "bg-gradient-to-r from-orange-400/80 to-orange-600/80 text-white backdrop-blur-sm";
    return "bg-gradient-to-r from-red-500/80 to-red-600/80 text-white backdrop-blur-sm";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading worksheets...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-500/5 to-cyan-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-2">
        {/* Header */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex flex-col lg:flex-row items-center justify-between mb-8 gap-4"
        >
          
          
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex items-center gap-3"
          >
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl shadow-xl">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                Worksheet Management
              </h1>
              <p className="text-slate-600 text-sm mt-1">Manage HAZOP analysis worksheets</p>
            </div>
          </motion.div>

          <div className="flex gap-4 md:flex-row flex-col">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(`/dashboard/node/${nodeId}/aiWorksheet`)}
            className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <ClipboardCheck size={20} />
            <span className="font-medium">AI Worksheet</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(`/dashboard/download/${nodeId}`)}
            className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl hover:from-emerald-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Download size={20} />
            <span className="font-medium">Download</span>
          </motion.button>
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          {[
            { title: "Total Worksheets", value: worksheets.length, icon: Activity, color: "from-blue-500 to-cyan-500" },
            { title: "High Risk Items", value: worksheets.filter(w => w.risk >= 15).length, icon: AlertTriangle, color: "from-red-500 to-pink-500" },
            { title: "Medium Risk Items", value: worksheets.filter(w => w.risk >= 6 && w.risk < 15).length, icon: Target, color: "from-amber-500 to-orange-500" },
            { title: "Low Risk Items", value: worksheets.filter(w => w.risk < 6).length, icon: Sparkles, color: "from-emerald-500 to-green-500" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-black/70 text-sm font-medium">{stat.title}</p>
                  <p className="text-3xl font-bold text-black mt-2">{stat.value}</p>
                </div>
                <div className={`p-3 bg-gradient-to-r ${stat.color} rounded-xl shadow-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Worksheets Table */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="p-2 border-b border-white/20 bg-gradient-to-r from-white/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl shadow-lg">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-black">HAZOP Worksheets</h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 backdrop-blur-sm border-b border-white/10">
                  {[
                    "Guide Word", "Parameter", "Deviation", "Causes", "Consequences", 
                    "Severity", "Likelihood", "Risk", "Safeguards", "Recommendations", "Actions"
                  ].map((header) => (
                    <th key={header} className="px-4 py-4 text-left text-sm font-semibold text-black/90 uppercase tracking-wider sticky top-0 bg-white/10 backdrop-blur-xl">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {worksheets.map((row, index) => (
                    <motion.tr 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-white/5 hover:bg-white/5 transition-all duration-300"
                    >
                      {/* Guide Word */}
                      <td className="px-4 py-4 min-w-[120px]">
                        <select
                          value={row.guideWord}
                          onChange={(e) => handleInputChange(index, "guideWord", e.target.value)}
                          className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                        >
                          <option value="" className="text-slate-800 bg-white">Select Guide Word</option>
                          {guideWordOptions.map((option) => (
                            <option key={option} value={option} className="text-slate-800 bg-white">{option}</option>
                          ))}
                        </select>
                      </td>

                      {/* Parameter */}
                      <td className="px-4 py-4 min-w-[120px]">
                        <select
                          value={row.parameter}
                          onChange={(e) => handleInputChange(index, "parameter", e.target.value)}
                          className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                        >
                          <option value="" className="text-slate-800 bg-white">Select Parameter</option>
                          {parameterOptions.map((option) => (
                            <option key={option} value={option} className="text-slate-800 bg-white">{option}</option>
                          ))}
                        </select>
                      </td>

                      {/* Deviation */}
                      <td className="px-4 py-4 min-w-[200px]">
                        <textarea
                          value={row.deviation}
                          onChange={(e) => handleInputChange(index, "deviation", e.target.value)}
                          placeholder="Auto-filled deviation"
                          className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 resize-none"
                          rows={3}
                        />
                      </td>

                      {/* Causes */}
                      <td className="px-4 py-4 min-w-[250px]">
                        <div className="space-y-3">
                          <select
                            onChange={(e) => handleMultiSelect(index, "causes", e.target.value)}
                            className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          >
                            <option value="" className="text-slate-800 bg-white">Select from suggestions</option>
                            {(row.causesOptions || []).map((option) => (
                              <option key={option} value={option} className="text-slate-800 bg-white">{option}</option>
                            ))}
                          </select>

                          <input
                            type="text"
                            placeholder="Add custom cause (Press Enter)"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleManualInput(index, "causes", e.target.value);
                                e.target.value = "";
                              }
                            }}
                            className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          />

                          <div className="flex flex-wrap gap-2">
                            {(row.causes || []).map((item) => (
                              <motion.span
                                key={item}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="bg-blue-500/20 backdrop-blur-sm border border-blue-500/30 text-black text-xs px-3 py-1 rounded-full flex items-center gap-2 hover:bg-blue-500/30 transition-all duration-300"
                              >
                                {item}
                                <button
                                  onClick={() => handleRemoveSelection(index, "causes", item)}
                                  className="hover:bg-red-500/30 rounded-full p-0.5 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </motion.span>
                            ))}
                          </div>
                        </div>
                      </td>

                      {/* Consequences */}
                      <td className="px-4 py-4 min-w-[250px]">
                        <div className="space-y-3">
                          <select
                            value={row.firstConsequence || ""}
                            onChange={(e) => handleInputChange(index, "firstConsequence", e.target.value)}
                            className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          >
                            <option value="" className="text-slate-800 bg-white">Select first part</option>
                            {(row.consequencesOptions || []).map((option) => (
                              <option key={option + "_first"} value={option} className="text-slate-800 bg-white">{option}</option>
                            ))}
                          </select>

                          <select
                            value={row.secondConsequence || ""}
                            onChange={(e) => handleInputChange(index, "secondConsequence", e.target.value)}
                            className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          >
                            <option value="" className="text-slate-800 bg-white">Select second part</option>
                            {(leadingConsequencesOptions || []).map((option) => (
                              <option key={option + "_second"} value={option} className="text-slate-800 bg-white">{option}</option>
                            ))}
                          </select>

                          <input
                            type="text"
                            placeholder="Add custom consequence (Press Enter)"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleManualInput(index, "consequences", e.target.value);
                                e.target.value = "";
                              }
                            }}
                            className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          />

                          <div className="flex flex-wrap gap-2">
                            {(row.consequences || []).map((item) => (
                              <motion.span
                                key={item}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="bg-purple-500/20 backdrop-blur-sm border border-purple-500/30 text-black text-xs px-3 py-1 rounded-full flex items-center gap-2 hover:bg-purple-500/30 transition-all duration-300"
                              >
                                {item}
                                <button
                                  onClick={() => handleRemoveSelection(index, "consequences", item)}
                                  className="hover:bg-red-500/30 rounded-full p-0.5 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </motion.span>
                            ))}
                          </div>
                        </div>
                      </td>

                      {/* Severity */}
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          min={1}
                          max={5}
                          value={row.severity}
                          onChange={(e) => handleInputChange(index, "severity", e.target.value)}
                          className="w-full p-3 text-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          placeholder="1-5"
                        />
                      </td>

                      {/* Likelihood */}
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          min={1}
                          max={5}
                          value={row.likelihood}
                          onChange={(e) => handleInputChange(index, "likelihood", e.target.value)}
                          className="w-full p-3 text-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                          placeholder="1-5"
                        />
                      </td>

                      {/* Risk */}
                      <td className="px-4 py-4">
                        <div className={`p-3 rounded-xl text-center font-bold text-lg shadow-lg ${getRiskColor(row.risk)}`}>
                          {row.risk || "0"}
                        </div>
                      </td>

                      {/* Safeguards & Recommendations */}
                      {["existingSafeguard", "recommendation"].map((field) => (
                        <td key={field} className="px-4 py-4 min-w-[250px]">
                          <div className="space-y-3">
                            <select
                              onChange={(e) => handleMultiSelect(index, field, e.target.value)}
                              className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                            >
                              <option value="" className="text-slate-800 bg-white">Select from suggestions</option>
                              {row[`${field}Options`]?.map((option) => (
                                <option key={option} value={option} className="text-slate-800 bg-white">{option}</option>
                              ))}
                            </select>

                            <input
                              type="text"
                              placeholder={`Add custom ${field === "existingSafeguard" ? "safeguard" : "recommendation"} (Press Enter)`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleManualInput(index, field, e.target.value);
                                  e.target.value = "";
                                }
                              }}
                              className="w-full p-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                            />

                            <div className="flex flex-wrap gap-2">
                              {(row[field] || []).map((item) => (
                                <motion.span
                                  key={item}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  className={`${field === "existingSafeguard" ? "bg-emerald-500/20 border-emerald-500/30 text-black" : "bg-amber-500/20 border-amber-500/30 text-black"} backdrop-blur-sm border text-xs px-3 py-1 rounded-full flex items-center gap-2 hover:bg-opacity-30 transition-all duration-300`}
                                >
                                  {item}
                                  <button
                                    onClick={() => handleRemoveSelection(index, field, item)}
                                    className="hover:bg-red-500/30 rounded-full p-0.5 transition-colors"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </motion.span>
                              ))}
                            </div>
                          </div>
                        </td>
                      ))}

                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleSave(index)}
                            className="p-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl hover:from-emerald-600 hover:to-green-700 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
                          >
                            <Save size={16} />
                          </motion.button>

                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDelete(index, row._id)}
                            className="p-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:from-red-600 hover:to-pink-700 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
                          >
                            <Trash2 size={16} />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Add Row Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddRow}
          className="mt-8 flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <Plus size={24} />
          <span className="font-semibold text-lg">Add New Worksheet</span>
        </motion.button>
      </div>
    </div>
  );
};

export default WorksheetPage;